"use client"

import { useEffect, useRef } from 'react'
import { LifeBuoy } from 'lucide-react'
import { create } from 'zustand'
import { usePathname, useRouter } from 'next/navigation'
import { toast } from 'sonner'
import { useStore } from '@/lib/store'
import { createClient } from '@/lib/supabase/client'
import { useT } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { playChime, unlockChime } from '@/lib/chime'
import { ALERT_POLL_MS, arrivals, latestOnly, seenMark, toAnnounce, unseenCount } from '@/lib/order-alerts'
import { SUPPORT_ANNOUNCED_KEY, SUPPORT_SEEN_KEY, stamped, supportAlertMessage } from '@/lib/support-alerts'
import type { SupportRequest } from '@/components/platform/support-dialog'

type Stamped = SupportRequest & { created_at: string }
type Alerts = { open: Stamped[]; seenAt: string | null }
const useAlerts = create<Alerts>(() => ({ open: [], seenAt: null }))

const onConsole = (pathname: string) => pathname.startsWith('/platform')

/** The menu's badge: how many help requests wait, and whether some came since this device last had the console in view. */
export function useSupportAlertBadge(): { count: number; pulse: boolean } {
  const open = useAlerts((s) => s.open)
  const seenAt = useAlerts((s) => s.seenAt)
  const pathname = usePathname()
  return { count: open.length, pulse: !onConsole(pathname) && unseenCount(open, seenAt) > 0 }
}

function markSeen(open: Stamped[]) {
  const current = useAlerts.getState().seenAt
  const mark = seenMark(open, current)
  if (mark === current || mark === null) return
  useAlerts.setState({ seenAt: mark })
  try { localStorage.setItem(SUPPORT_SEEN_KEY, mark) } catch { /* private mode: the mark lives for this visit */ }
}

/** One tab at a time reads and moves this device's „announced" mark: two tabs answered at once could
 *  both read the old mark and both chime (Codex, 1.45 #3). No Web Locks here: the tab speaks for itself. */
function oneTabAtATime(run: () => void) {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks) { run(); return }
  void locks.request(SUPPORT_ANNOUNCED_KEY, async () => { run() }).catch(() => {})
}

/** The installed app's icon (Windows, macOS, an iPhone's home screen) carries the count; Android shows
 *  icon numbers only for notifications — the admin's phone has Telegram for that (161). */
function appBadge(n: number) {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> }
  try {
    if (n > 0) void nav.setAppBadge?.(n)?.catch(() => {})
    else void nav.clearAppBadge?.()?.catch(() => {})
  } catch { /* no badging here */ }
}

/**
 * A shop's help request reaches the platform admin by itself (lib/support-alerts.ts):
 * every 30 s — and when the window comes back — this asks `support_requests()`;
 * one that was not there before chimes and says so, with „ნახვა". Mounted twice,
 * never at once: in the shop shell (an admin with a shop, from the store's
 * `platformGate === 'ok'`) and on the console itself (`enabled` = the page's own check,
 * `onList` keeps its list live). Draws nothing; the menus read `useSupportAlertBadge`.
 */
export function SupportAlerts({ enabled: given, onList }: { enabled?: boolean; onList?: (list: SupportRequest[]) => void }) {
  const t = useT()
  const router = useRouter()
  const pathname = usePathname()
  // 1.48 — only a session past the phone's code may ask support_requests()
  // (167 refuses it below 'ok'); an admin still at 'verify' would poll errors.
  const ready = useStore((s) => s.platformGate === 'ok')
  const enabled = given ?? ready
  const here = onConsole(pathname)
  const open = useAlerts((s) => s.open)
  // what the poll's closure must read as it is now, not as it was when it started
  const now = useRef({ t, router, here, onList })
  now.current = { t, router, here, onList }

  // the browser plays a page's sound only after a press on it: the first press wakes it
  useEffect(() => {
    if (!enabled) return
    const wake = () => unlockChime()
    window.addEventListener('pointerdown', wake, { capture: true })
    window.addEventListener('keydown', wake, { capture: true })
    return () => {
      window.removeEventListener('pointerdown', wake, { capture: true })
      window.removeEventListener('keydown', wake, { capture: true })
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) { useAlerts.setState({ open: [], seenAt: null }); return }
    let seenAt: string | null = null
    try { seenAt = localStorage.getItem(SUPPORT_SEEN_KEY) } catch { /* no storage: every waiting request counts as unseen */ }
    useAlerts.setState({ open: [], seenAt })
    const supabase = createClient()
    let previous: Set<string> | null = null
    let live = true

    const ask = async () => supabase.rpc('support_requests')
    // checks may overlap (the tick, the window coming back): only the newest answer counts
    const check = latestOnly(ask, ({ data, error }) => {
      // offline or refused: keep what is shown, try again on the next tick
      if (!live || error || !data) return
      const rows = data as SupportRequest[]
      const list = rows.map(stamped)
      const came = arrivals(previous, list)
      previous = new Set(list.map((r) => r.id))
      useAlerts.setState({ open: list })
      now.current.onList?.(rows)
      if (came.length === 0) return
      // another tab of this device may have said so already: one chime, one message per request
      oneTabAtATime(() => {
        if (!live) return
        let said: string | null = null
        try { said = localStorage.getItem(SUPPORT_ANNOUNCED_KEY) } catch { /* no storage: this tab speaks for itself */ }
        const { fresh, mark } = toAnnounce(came, said)
        if (fresh.length === 0) return
        if (mark) try { localStorage.setItem(SUPPORT_ANNOUNCED_KEY, mark) } catch { /* as above */ }
        const { t: tr, router: go, here: atConsole } = now.current
        playChime()
        const m = supportAlertMessage(fresh)
        toast(tr(m.key, m.vars), {
          duration: 15_000,
          action: atConsole ? undefined : { label: tr('sup.alert.view'), onClick: () => go.push('/platform') },
        })
      })
    })

    void check()
    const tick = window.setInterval(() => void check(), ALERT_POLL_MS)
    const onVisible = () => { if (document.visibilityState === 'visible') void check() }
    // the console seen in another tab of this device: the pulse stops here too
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SUPPORT_SEEN_KEY || !e.newValue) return
      const current = useAlerts.getState().seenAt
      if (current === null || Date.parse(e.newValue) > Date.parse(current)) useAlerts.setState({ seenAt: e.newValue })
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('storage', onStorage)
    return () => {
      live = false
      window.clearInterval(tick)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('storage', onStorage)
    }
  }, [enabled])

  // the console in view: everything it shows is seen — the pulse stops, the count stays
  useEffect(() => {
    if (enabled && here) markSeen(open)
  }, [enabled, here, open])

  // the app's icon follows the count; leaving (a sign-out, the other mount) clears it
  useEffect(() => {
    if (!enabled) return
    appBadge(open.length)
  }, [enabled, open.length])
  useEffect(() => () => appBadge(0), [])

  return null
}

/**
 * The header's „2 დახმარება" — computers and tablets. The sidebar's „პლატფორმა"
 * sits below the fold of a 1440×900 or 1366×768 menu (seen on the 1.45 preview),
 * so its count alone could go unseen; this pill is in sight on every page while
 * a request waits, and opens the console. Nothing when none waits (or for a shop).
 */
export function SupportPill() {
  const t = useT()
  const router = useRouter()
  const { count, pulse } = useSupportAlertBadge()
  if (count === 0) return null
  return (
    <button
      type="button"
      data-support-pill
      data-pulse={pulse ? 'true' : 'false'}
      onClick={() => router.push('/platform')}
      title={t('sup.alert.badge', { n: count })}
      className="hidden md:flex relative h-9 px-3 rounded-xl border border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-500/15 text-[13px] font-bold items-center gap-1.5 shrink-0 transition-colors"
    >
      <LifeBuoy className="size-4" />
      {t('sup.alert.pill', { n: count })}
      {pulse && (
        <span aria-hidden className="absolute -top-1 -right-1 flex size-2.5">
          <span className="absolute inset-0 rounded-full bg-indigo-500/60 animate-ping motion-reduce:hidden" />
          <span className="relative size-2.5 rounded-full bg-indigo-600 ring-2 ring-background" />
        </span>
      )}
    </button>
  )
}

/**
 * The waiting count next to „პლატფორმა" (and on the phone's „მეტი"). It pulses
 * only while a request came after this device last had the console in view —
 * the order badge's ring, never under reduced motion. The platform item's own
 * indigo, not red: red is for debts and deletes.
 */
export function SupportBadge({ count, pulse, className }: { count: number; pulse: boolean; className?: string }) {
  const t = useT()
  if (count === 0) return null
  return (
    <span data-support-badge data-pulse={pulse ? 'true' : 'false'} className={cn('relative inline-flex shrink-0', className)}>
      {pulse && <span aria-hidden className="absolute inset-0 rounded-full bg-indigo-500/60 animate-ping motion-reduce:hidden" />}
      <span aria-hidden className="relative flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[12px] font-bold leading-none text-white ring-2 ring-background">
        {count > 99 ? '99+' : count}
      </span>
      <span className="sr-only">{t('sup.alert.badge', { n: count })}</span>
    </span>
  )
}
